import { isEqual } from "lodash-es"
import type {
  AmnOutgoingEvent,
  CallStateEvent,
  CallStateParticipant,
  CallStateParticipantRole,
  CallStateStatus,
} from "./outgoingEvents"
import { getMembersList, whenMembersUpdated } from "../../../ucs"
import type { VideoCallMember } from "@teladoc/ucs-client-react"
import type {
  SessionConnectFailedEventHandler,
  SessionDisconnectedEventHandler,
} from "../IFrameEvents"

export const initCallStateEvents = (
  postMessage: (event: AmnOutgoingEvent) => void
) => {
  let currentCallState: CallStateEvent = {
    type: "amn:child/callState",
    payload: {
      status: "initializing",
      // We're always ready to refresh
      refreshReady: true,
      // We never support multiple interpreters
      multiInterpreterEnabled: false,
      callFailureMessage: null,
      participants: [],
    },
  }

  const isConnected = () => currentCallState.payload.status === "connected"

  const sendCallState = () => postMessage(currentCallState)

  // Immediately send the initial state
  sendCallState()

  const setCallState = (newState: CallStateEvent) => {
    // We expect to get more updates than we care about, so only send when
    // something has actually changed.
    if (!isEqual(newState, currentCallState)) {
      currentCallState = newState
      sendCallState()
    }
  }

  const updateCallStatus = (status: CallStateStatus) => {
    setCallState({
      ...currentCallState,
      payload: {
        ...currentCallState.payload,
        status,
      },
    })
  }

  const onSessionConnecting = () => updateCallStatus("connecting")

  const onSessionConnected = () => {
    updateCallStatus("connected")
    getMembersList()
      .then(onUcsMembersUpdated)
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.warn("Failed to get members on AMN session connected", { e })
      })
  }

  const onSessionConnectFailed: SessionConnectFailedEventHandler = ({
    reason,
  }) => {
    setCallState({
      ...currentCallState,
      payload: {
        ...currentCallState.payload,
        status: "finished",
        callFailureMessage:
          reason ?? "Connect failed due to an unexpected error",
      },
    })
  }

  const onSessionDisconnected: SessionDisconnectedEventHandler = ({
    sessionEndedArgs,
  }) => {
    const callFailureMessage =
      sessionEndedArgs.reason === "Error"
        ? sessionEndedArgs.info ?? "Session ended due to an unexpected error"
        : null
    setCallState({
      ...currentCallState,
      payload: {
        ...currentCallState.payload,
        status: "finished",
        callFailureMessage,
      },
    })
  }

  const { getMemberArrivalTime, updateMemberArrivalTimes, clearArrivalTimes } =
    manageArrivalTimes()

  const onUcsMembersUpdated = (members: VideoCallMember[]) => {
    updateMemberArrivalTimes(members)
    const participants = parseMembersToParticipants(
      members,
      getMemberArrivalTime
    )
    setCallState({
      ...currentCallState,
      payload: {
        ...currentCallState.payload,
        participants,
      },
    })
  }

  const unwatchMembersUpdated = whenMembersUpdated(onUcsMembersUpdated)

  const handleGetCallStateEvent = () => sendCallState()

  const dispose = () => {
    unwatchMembersUpdated()
    clearArrivalTimes()
  }

  return {
    isConnected,
    handleGetCallStateEvent,
    sendCallState,
    onSessionConnected,
    onSessionConnecting,
    onSessionConnectFailed,
    onSessionDisconnected,
    dispose,
  }
}

export const parseMembersToParticipants = (
  members: VideoCallMember[],
  getMemberArrivalTime: (member: VideoCallMember) => string
): CallStateParticipant[] =>
  members.map((member: VideoCallMember): CallStateParticipant => {
    return {
      id: member.id,
      name: member.displayName,
      role: parseParticipantRoleForMember(member),
      joinedAt: getMemberArrivalTime(member),
      // The member will be removed from the list when they leave
      leftAt: null,
      videoEnabled: member.mediaStreams.some(
        ({ stream }) => stream.isAvailable
      ),
      audioEnabled: !member.isMuted,
    }
  })

export const parseParticipantRoleForMember = (
  member: VideoCallMember
): CallStateParticipantRole =>
  member.isLocal ? "interpreter" : member.isProvider ? "client" : "other"

/**
 * Times are stored as ISO strings to match the format expected by AMN.
 */
export const manageArrivalTimes = () => {
  const memberArrivalTimes: Map<string, string> = new Map()

  const createTimestamp = () => new Date().toISOString()

  const getMemberArrivalTime = ({ id }: VideoCallMember): string =>
    memberArrivalTimes.get(id) ?? createTimestamp()

  const updateMemberArrivalTimes = (members: VideoCallMember[]) =>
    members.forEach(({ id }) => {
      if (!memberArrivalTimes.has(id)) {
        memberArrivalTimes.set(id, createTimestamp())
      }
    })

  const clearArrivalTimes = () => memberArrivalTimes.clear()

  return { getMemberArrivalTime, updateMemberArrivalTimes, clearArrivalTimes }
}
